"use client"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Calendar } from "@/shared/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components/ui/form"
import { Textarea } from "@/shared/components/ui/textarea"
import { MoveRight, CalendarDays } from "lucide-react"
import { cn } from "@/shared/lib/utils/UtilsExports"
import { format } from "date-fns"
import type { TravelInsuranceFormData } from "@/shared/types/Forms"
import { useToast } from "@/shared/components/Toast"

export const TravelInsuranceForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  
  // Safe toast context handling
  const { showToast } = useToast()

  const insuranceForm = useForm<TravelInsuranceFormData>({
    mode: 'onChange',
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      nationality: "",
      travelDate: undefined,
      destination: "",
      countriesLayover: "",
      numberOfPax: "",
      additionalInfo: "",
    },
  })

  // Error boundary for form initialization
  if (formError) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">Form initialization error</div>
        <button 
          onClick={() => setFormError(null)} 
          className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
        >
          Retry
        </button>
      </div>
    )
  }

  const onInsuranceSubmit = async (data: TravelInsuranceFormData) => {
    if (!data) {
      console.error('Form data is undefined')
      return
    }
    
    setIsSubmitting(true)

    try {
      // Validate required fields before submission
      const requiredFields = [
        { field: data.name, name: 'Name' },
        { field: data.email, name: 'Email' },
        { field: data.phone, name: 'Phone' },
        { field: data.nationality, name: 'Nationality' },
        { field: data.destination, name: 'Destination' },
        { field: data.numberOfPax, name: 'Number of Pax' },
        { field: data.travelDate, name: 'Travel Date' }
      ]
      
      const missingFields = requiredFields.filter(item => !item.field || (typeof item.field === 'string' && !item.field.trim()))
      
      if (missingFields.length > 0) {
        const fieldNames = missingFields.map(f => f.name).join(', ')
        throw new Error(`Please fill in all required fields: ${fieldNames}`)
      }

      const payload = {
        applicant_name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone?.trim() || '',
        nationality: data.nationality?.trim() || '',
        destination: data.destination.trim(),
        start_date: data.travelDate ? format(data.travelDate, 'yyyy-MM-dd') : null,
        end_date: data.travelDate ? format(data.travelDate, 'yyyy-MM-dd') : null,
        number_of_pax: data.numberOfPax?.trim() || '1',
        countries_layover: data.countriesLayover?.trim() || '',
        additional_info: data.additionalInfo?.trim() || ''
      }
      const response = await fetch('/api/v2/forms.php?type=insurance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      if (result.success) {
        // Show success toast
        showToast({
          type: 'success',
          title: 'Application Submitted!',
          message: 'Your travel insurance application has been submitted successfully!',
          duration: 5000
        })
        
        // Reset form to initial values
        try {
          insuranceForm.reset({
            name: "",
            email: "",
            phone: "",
            nationality: "",
            travelDate: undefined,
            destination: "",
            countriesLayover: "",
            numberOfPax: "",
            additionalInfo: "",
          })
        } catch (resetError) {
          console.warn('Form reset error:', resetError)
        }
      } else {
        showToast({
          type: 'error',
          title: 'Submission Failed',
          message: result.error || result.message || 'Failed to submit application. Please try again.',
          duration: 5000
        })
      }
    } catch (error) {
      console.error('Submit error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit application. Please try again.'
      showToast({
        type: 'error',
        title: 'Submission Error',
        message: errorMessage,
        duration: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Add error boundary check
  if (!insuranceForm) {
    return <div>Loading form...</div>
  }

  try {
    return (
      <Form {...insuranceForm}>
        <form onSubmit={insuranceForm.handleSubmit(onInsuranceSubmit)} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={insuranceForm.control}
            name="name"
            rules={{ required: "Name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Name *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="John Doe"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={insuranceForm.control}
            name="email"
            rules={{ 
              required: "Email is required",
              validate: {
                isEmail: (value) => {
                  if (!value || typeof value !== 'string') return 'Email is required'
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                  return emailRegex.test(value.toLowerCase()) || 'Please enter a valid email address'
                }
              }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Email Address *</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={insuranceForm.control}
            name="phone"
            rules={{ 
              required: "Phone number is required",
              validate: {
                isPhone: (value) => {
                  if (!value || typeof value !== 'string') return 'Phone number is required'
                  const phoneRegex = /^[+]?[(]?[\d\s\-()]{10,}$/
                  return phoneRegex.test(value.trim()) || 'Please enter a valid phone number'
                }
              }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Phone Number *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="+1234567890"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={insuranceForm.control}
            name="nationality"
            rules={{ required: "Nationality is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Nationality *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Filipino"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={insuranceForm.control}
            name="travelDate"
            rules={{ required: "Travel date is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Travel Date *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full h-11 justify-start text-left font-normal border-gray-300",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        <CalendarDays className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">
                          {field.value && field.value instanceof Date ? format(field.value, "PPP") : "Pick a date"}
                        </span>
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar 
                      mode="single" 
                      selected={field.value} 
                      onSelect={(date) => {
                        try {
                          field.onChange(date)
                        } catch (error) {
                          console.warn('Date selection error:', error)
                        }
                      }} 
                      disabled={(date) => {
                        try {
                          return date < new Date()
                        } catch {
                          return false
                        }
                      }}
                      initialFocus 
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={insuranceForm.control}
            name="numberOfPax"
            rules={{ 
              required: "Number of pax is required",
              validate: {
                isNumber: (value) => {
                  if (!value || typeof value !== 'string') return 'Number of pax is required'
                  const num = parseInt(value.trim())
                  return (num > 0 && num.toString() === value.trim()) || 'Please enter a valid number (1 or more)'
                }
              }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Number of Pax *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., 2"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={insuranceForm.control}
            name="destination"
            rules={{ required: "Destination is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Destination *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Japan"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={insuranceForm.control}
            name="countriesLayover"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">
                  Countries Including Layover (Optional)
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Singapore, Thailand"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={insuranceForm.control}
          name="additionalInfo"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-gray-700">Additional Information</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional information or special requests..."
                  className="border-amber-300 focus:border-amber-500 focus:ring-amber-500 autumn-input resize-none"
                  rows={4}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-2">
          <Button 
            type="submit" 
            variant="ghost"
            disabled={isSubmitting}
            className="btn-autumn-primary h-12 font-semibold px-8 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)', color: 'white' }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)'
                e.currentTarget.style.color = 'white'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
              e.currentTarget.style.color = 'white'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
            <MoveRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </form>
    </Form>
    )
  } catch (error) {
    console.error('TravelInsuranceForm render error:', error)
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">Form component error occurred</div>
        <p className="text-gray-600 mb-4">Please refresh the page and try again.</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
        >
          Refresh Page
        </button>
      </div>
    )
  }
}
